import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueueStore } from "@/lib/store";
import { Badge } from "@/components/ui/badge";
import { PatientCard } from "../queue/PatientCard";
import { useEffect } from "react";
import { useAuthStore } from '@/lib/auth';
import { Link } from "react-router-dom";
import { subDays, isSameDay } from 'date-fns';

export function DoctorsList() {
  const { doctors, getPatientsByDoctor, fetchDoctors } = useQueueStore();
  const { user } = useAuthStore();

  useEffect(() => {
    // Fetch doctors when component mounts
    fetchDoctors();
  }, [fetchDoctors]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {doctors.map((doctor) => {
        // Only show patients assigned to this doctor from yesterday or today
        const assignedPatients = getPatientsByDoctor(doctor.id).filter(patient => {
          if (!patient.registeredAt) return false;
          const registeredDate = new Date(patient.registeredAt);
          const today = new Date();
          const yesterday = subDays(today, 1);
          // Show if registered today or yesterday
          return isSameDay(registeredDate, today) || isSameDay(registeredDate, yesterday);
        });
        return (
          <Card key={doctor.id}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">{doctor.name}</CardTitle>
                <Badge 
                  variant="outline" 
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  Available
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">{doctor.specialization}</div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Current patients</h3>
                {assignedPatients.length === 0 ? (
                  <div className="text-sm text-gray-500">No patients assigned</div>
                ) : (
                  <div className="space-y-2">
                    {assignedPatients.map((patient) => (
                      <div key={patient.id} className="border rounded px-2 py-1 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Link to={`/patient/${patient.id}`} className="hover:text-hospital-primary transition-colors font-medium">
                              {patient.name}
                            </Link>
                            <span className="text-xs text-gray-500">{patient.age} yrs, {patient.gender}</span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${patient.status === 'in-consultation' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{patient.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        </div>
                        

                        {patient.services && patient.services.length > 0 && (
                          <div className="mt-1 flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {patient.services.map((service, idx) => (
                                <span
                                  key={idx}
                                  className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 text-xs font-medium"
                                >
                                  {service.service_name}
                                </span>
                              ))}
                            </div>
                            <div>
                              {(() => {
                                const servicesCost = patient.services?.reduce((total, service) => total + service.price, 0) || 0;
                                const totalAmount = servicesCost;
                                const amountPaid = patient.amountPaid;
                                
                                // For completed patients with no services (bill generated), show "Bills Cleared"
                                if (patient.status === 'completed' && servicesCost === 0) {
                                  return (
                                    <span className="text-xs text-white bg-blue-500 px-2 py-1 rounded-full font-medium">
                                      Bills Cleared
                                    </span>
                                  );
                                }
                                
                                // If no payment has been entered at all (amountPaid is null/undefined/0 and no services)
                                if ((amountPaid === null || amountPaid === undefined || amountPaid === 0) && servicesCost === 0) {
                                  return null; // Don't show payment information
                                }
                                
                                // If payment has been entered and there are services
                                if (totalAmount > 0) {
                                  if (amountPaid >= totalAmount) {
                                    return (
                                      <span className="text-xs text-green-600 font-medium">
                                        Paid: ₹{amountPaid}
                                      </span>
                                    );
                                  } else {
                                    return (
                                      <span className="text-xs text-red-600 font-medium">
                                        Due: ₹{totalAmount - amountPaid}
                                      </span>
                                    );
                                  }
                                }
                                
                                return null; // Don't show anything if no services and no payment
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
